import type { Currency } from '../types/currency';

/**
 * Get the number of decimals for a currency.
 *
 * e.g.
 * - AUD: 2
 * - GBP: 2
 * - JPY: 0 (no decimals)
 *
 * @param currency - ISO4217 currency code
 * @returns number of decimals for the currency
 */
function getCurrencyDecimals(currency: Currency): number {
  // ECMA-402 spec guarantees that `maximumFractionDigits` is always defined
  return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits!;
}

/**
 * Get the number of minor units for a currency.
 *
 * e.g.
 * - AUD: 100
 * - GBP: 100
 * - JPY: 1 (no minor units)
 *
 * @param currency - ISO4217 currency code
 * @returns number of minor units for the currency
 */
function getCurrencyMinorUnits(currency: Currency): number {
  return 10 ** getCurrencyDecimals(currency);
}

/**
 * Converts a currency amount from minor units to major units (e.g. cents to dollars).
 * @param amount - currency amount in minor units
 * @param currency - ISO4217 currency code
 * @returns currency amount in major units
 */
function toCurrencyMajorUnits(amount: number, currency: Currency) {
  return amount / getCurrencyMinorUnits(currency);
}

/**
 * Formats a currency amount as a float with the correct number of decimal places.
 *
 * NOTE: This function is intended for use where a machine-readable float is required, such as tracking attributes or input[type="number"] fields.
 * Localised currency formatting should be handled separately as part of translations.
 *
 * @param amount - currency amount in minor units
 * @param currency - ISO4217 currency code
 * @returns currency amount as a float with the correct number of decimal places
 */
function formatCurrencyAmountAsFloat(amount: number, currency: Currency) {
  return toCurrencyMajorUnits(amount, currency).toFixed(getCurrencyDecimals(currency));
}

export { formatCurrencyAmountAsFloat, getCurrencyDecimals, getCurrencyMinorUnits, toCurrencyMajorUnits };
